import { FC, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogActions } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
// Functions/Constants/Enum
import HttpService from 'shared/services/Http.service';
import { API_CONFIG } from 'shared/constants/api';
import AuthService from 'shared/services/Auth.service';
import { createAction } from 'shared/util/Utility';
import * as actionTypes from 'store/action-types';
import { ROUTES } from 'shared/constants/constant';
// components
import AMBGSelect from 'shared/components/AMBGSelect/AMBGSelect';
import AMBGButton from 'shared/components/AMBGButton/AMBGButton';
// Types/Interface
import { IDropDownList, IDropDownOption } from 'shared/interface';
import { IState, ICommonState } from 'shared/interface/state';
// Assets
import * as Sx from 'assets/styles/Common.style';

type IProps = {
	onCancel: () => void;
};

const CompanySelect: FC<IProps> = (props) => {
	const { onCancel } = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const commonState = useSelector((state: IState) => state.common as ICommonState);
	const [dropdownList, setDropdownList] = useState<IDropDownList>({});
	const [isLoading, setLoading] = useState<boolean>(false);
	const [selectedCompany, setSelectedCompany] = useState<IDropDownOption>({} as IDropDownOption);

	const fetchDropdownList = useCallback(async () => {
		try {
			setLoading(true);
			const response = await HttpService.get(API_CONFIG.dropDownOption);
			setDropdownList(response.data);
			setLoading(false);
		} catch (error) {
			console.error(error);
			setLoading(false);
		}
	}, []);

	const handleCompanySelect = async () => {
		try {
			// fetch selected company master fields
			const response = await HttpService.get(`${API_CONFIG.masterFields}/${selectedCompany.value}`);
			// store selected company and their master fields
			const companyData = {
				selectedCompany: selectedCompany,
				selectedCompanyFields: response.data
			};
			AuthService.setLocalStorage('selectedCompanyData', companyData);
			dispatch(createAction(actionTypes.SELECTED_COMPANY_DATA, companyData));
			onCancel();
			navigate(ROUTES.MY_SALES);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		fetchDropdownList();
	}, [fetchDropdownList]);

	useEffect(() => {
		!isEmpty(commonState.selectedCompany) && setSelectedCompany(commonState.selectedCompany);
	}, [commonState.selectedCompany]);

	const handleOnChange = (id: number) => {
		const company = (dropdownList['company'] as IDropDownOption[]).find((companyObj) => companyObj.value === id);
		setSelectedCompany(company as IDropDownOption);
	};

	return (
		<Dialog
			open={true}
			fullWidth
			maxWidth='xs'
			PaperProps={{ sx: { borderRadius: '10px' } }}
			data-testid='selectCompanyDialog'
			onClose={onCancel}
		>
			{!isLoading && (
				<>
					<DialogContent>
						<AMBGSelect
							key='company'
							label='Select Company'
							placeholder='Select Company'
							data-testid='selectCompany'
							menuItem={dropdownList['company'] || []}
							value={selectedCompany.value || ''}
							onChange={({ target: { value } }) => handleOnChange(value as number)}
						/>
					</DialogContent>
					<DialogActions sx={{ alignSelf: 'center', padding: '0 20px 20px' }}>
						<AMBGButton
							text={'Cancel'}
							sx={Sx.lightPrimaryBtn}
							onClick={onCancel}
							data-testid='cancelBtn'
						/>
						<AMBGButton
							text={'Confirm'}
							onClick={handleCompanySelect}
							loading={false}
							disabled={isEmpty(selectedCompany)}
							data-testid='confirmBtn'
						/>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};

export default CompanySelect;
